import React, { useEffect, useState, useCallback } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import AdminRouteGuard from '../../components/admin/AdminRouteGuard';
import AdminConfirmDialog from '../../components/admin/AdminConfirmDialog';
import { sb } from '../../supabase';
import { logAdminAction } from '../../lib/adminAudit';

const FONT_HEADING = "'Cormorant Garamond', Georgia, serif";
const FONT_BODY    = "'Tajawal', sans-serif";

const fmtTime = d => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const CSS = `
  .st-page { padding: 36px 32px; max-width: 720px; }
  .st-page-title { margin: 0 0 4px; font-size: 26px; font-weight: 400; color: rgba(0,0,0,0.88); font-family: ${FONT_HEADING}; line-height: 1.1; }
  .st-page-sub { margin: 0 0 28px; font-size: 12px; color: rgba(0,0,0,0.38); font-family: ${FONT_BODY}; }
  .st-card { background: var(--bg-raised, #fff); border: 1px solid rgba(0,0,0,0.07); border-radius: 10px; padding: 20px; margin-bottom: 14px; }
  .st-card-title { margin: 0 0 16px; font-size: 10px; font-weight: 600; letter-spacing: 1.6px; text-transform: uppercase; color: rgba(0,0,0,0.38); font-family: ${FONT_BODY}; }
  .st-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
  .st-row:last-child { margin-bottom: 0; }
  .st-label { font-size: 13px; color: rgba(0,0,0,0.75); font-family: ${FONT_BODY}; }
  .st-sub { font-size: 11px; color: rgba(0,0,0,0.38); font-family: ${FONT_BODY}; margin-top: 2px; }
  .st-toggle { appearance: none; width: 40px; height: 22px; border-radius: 99px; background: rgba(0,0,0,0.15); cursor: pointer; position: relative; outline: none; transition: background 0.15s; flex-shrink: 0; }
  .st-toggle:checked { background: #27725a; }
  .st-toggle::after { content: ''; position: absolute; width: 16px; height: 16px; border-radius: 50%; background: #fff; top: 3px; left: 3px; transition: transform 0.15s; }
  .st-toggle:checked::after { transform: translateX(18px); }
  .st-input { padding: 8px 12px; background: var(--bg-subtle, #F5F2EE); border: 1px solid rgba(0,0,0,0.09); border-radius: 8px; font-size: 14px; color: rgba(0,0,0,0.80); font-family: ${FONT_BODY}; outline: none; width: 80px; text-align: center; }
  .st-input:focus { border-color: rgba(0,0,0,0.22); }
  .st-input-wide { width: 100%; box-sizing: border-box; margin-top: 8px; padding: 9px 12px; background: var(--bg-subtle, #F5F2EE); border: 1px solid rgba(0,0,0,0.09); border-radius: 8px; font-size: 13px; color: rgba(0,0,0,0.80); font-family: ${FONT_BODY}; outline: none; }
  .st-input-wide:focus { border-color: rgba(0,0,0,0.22); }
  .st-btn { min-height: 34px; padding: 0 14px; border-radius: 7px; font-size: 12px; cursor: pointer; font-family: ${FONT_BODY}; white-space: nowrap; border: 1px solid rgba(0,0,0,0.09); background: transparent; color: rgba(0,0,0,0.55); transition: all 0.12s; }
  .st-btn:hover { background: rgba(0,0,0,0.04); }
  .st-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .st-btn-primary { background: #1a1814; color: #fff; border-color: #1a1814; font-weight: 600; }
  .st-btn-primary:hover { opacity: 0.88; background: #1a1814; }
  .st-btn-danger { color: #c0392b; border-color: rgba(192,57,43,0.20); }
  .st-btn-danger:hover { background: rgba(192,57,43,0.06); }
  .st-flash { position: fixed; bottom: 80px; left: 50%; transform: translateX(-50%); background: #1a1814; color: #fff; padding: 10px 22px; border-radius: 99px; font-size: 12px; font-family: ${FONT_BODY}; z-index: 999; white-space: nowrap; pointer-events: none; }
  .st-divider { border: none; border-top: 1px solid rgba(0,0,0,0.06); margin: 14px 0; }
  .st-audit-row { padding: 10px 0; border-bottom: 1px solid rgba(0,0,0,0.05); }
  .st-audit-row:last-child { border-bottom: none; }
  .st-mono { font-family: monospace; font-size: 11px; color: rgba(0,0,0,0.55); }
  @media (max-width: 900px) { .st-page { padding: 22px 16px; } }
`;

function SectionCard({ title, children }) {
  return (
    <div className="st-card">
      {title && <p className="st-card-title">{title}</p>}
      {children}
    </div>
  );
}

export default function AdminSettings({ user, profile, lang, ...rest }) {
  const isAr = lang === 'ar';
  const isSuperAdmin = profile?.role === 'super_admin';

  const [settings, setSettings]   = useState({});
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState({});
  const [flashMsg, setFlashMsg]   = useState('');
  const [confirm, setConfirm]     = useState(null);
  const [auditLog, setAuditLog]   = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);

  // Fee editor local state
  const [feeValue, setFeeValue]   = useState('');
  // USD→SAR rate editor local state
  const [rateValue, setRateValue] = useState('');
  // CNY per USD (display rate) editor local state
  const [cnyValue, setCnyValue]   = useState('');
  // Maintenance message local state
  const [maintMsg, setMaintMsg]   = useState('');
  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole]   = useState('admin');

  const showFlash = msg => { setFlashMsg(msg); setTimeout(() => setFlashMsg(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await sb.from('admin_settings').select('*');
    const map = {};
    (data || []).forEach(r => { map[r.key] = r.value; });
    setSettings(map);
    setFeeValue(String(map.platform_fee_pct?.value ?? 5));
    setRateValue(String(map.usd_sar_rate?.rate ?? 3.79));
    setCnyValue(String(map.cny_usd_rate?.rate ?? 7.1));
    setMaintMsg(map.maintenance_mode?.message || '');
    setLoading(false);
  }, []);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    // `notes` carries the only readable detail on system rows — a provider
    // failure writes the task and the provider's own message there, and the row
    // is useless without it.
    const { data } = await sb.from('audit_log').select('id, created_at, action, entity_type, entity_id, notes, actor:actor_id(full_name, email)').order('created_at', { ascending: false }).limit(100);
    setAuditLog(data || []);
    setAuditLoading(false);
  }, []);

  useEffect(() => { load(); loadAudit(); }, [load, loadAudit]);

  const saveSetting = async (key, newValue) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    const before = settings[key];
    await sb.from('admin_settings').update({ value: newValue, updated_at: new Date().toISOString(), updated_by: user.id }).eq('key', key);
    await logAdminAction({ actorId: user.id, action: 'settings_update', entityType: 'admin_settings', entityId: key, beforeState: before, afterState: newValue });
    await load();
    setSaving(prev => ({ ...prev, [key]: false }));
    showFlash(isAr ? 'تم الحفظ' : 'Saved');
  };

  const doFeeUpdate = async () => {
    const parsed = parseFloat(feeValue);
    if (isNaN(parsed) || parsed < 0 || parsed > 100) { showFlash(isAr ? 'قيمة غير صالحة' : 'Invalid value'); return; }
    await saveSetting('platform_fee_pct', { value: parsed });
    setConfirm(null);
  };

  // The USD→SAR rate goes through admin_set_usd_sar_rate(), not saveSetting():
  // this one DOES price payments (payment_quote locks it onto every offer
  // accepted and invoice issued from now on), so the server checks the range
  // (3.50–4.50) and records who changed it.
  const doRateUpdate = async () => {
    const parsed = parseFloat(rateValue);
    if (isNaN(parsed) || parsed < 3.5 || parsed > 4.5) { showFlash(isAr ? 'السعر يجب أن يكون بين 3.50 و 4.50' : 'Rate must be between 3.50 and 4.50'); return; }
    setSaving(prev => ({ ...prev, usd_sar_rate: true }));
    const before = settings.usd_sar_rate;
    const { data, error } = await sb.rpc('admin_set_usd_sar_rate', { p_rate: parsed });
    if (error) {
      console.error('[AdminSettings] admin_set_usd_sar_rate failed:', error.code || '', error.message);
      showFlash((isAr ? 'تعذّر الحفظ: ' : 'Could not save: ') + (error.message || ''));
    } else {
      await logAdminAction({ actorId: user.id, action: 'settings_update', entityType: 'admin_settings', entityId: 'usd_sar_rate', beforeState: before, afterState: data });
      showFlash(isAr ? 'تم الحفظ' : 'Saved');
    }
    await load();
    setSaving(prev => ({ ...prev, usd_sar_rate: false }));
    setConfirm(null);
  };

  // CNY per USD goes through admin_set_cny_usd_rate() (range 6.00–8.50 checked
  // on the server, admins only). It is NOT display-only any more: every store
  // price (market_delivered_core, market-checkout) converts supplier CNY at it.
  // Since 2026-11-02 the fx-cny-rate job sets it daily from the ECB rate; a
  // hand-set rate switches to manual mode (the job then only reports) until
  // admin_set_cny_rate_mode(false) switches back.
  const doCnyMode = async (manual) => {
    setSaving(prev => ({ ...prev, cny_rate_auto: true }));
    const { error } = await sb.rpc('admin_set_cny_rate_mode', { p_manual: manual });
    if (error) {
      console.error('[AdminSettings] admin_set_cny_rate_mode failed:', error.code || '', error.message);
      showFlash((isAr ? 'تعذّر الحفظ: ' : 'Could not save: ') + (error.message || ''));
    } else showFlash(isAr ? 'تم الحفظ' : 'Saved');
    await load();
    setSaving(prev => ({ ...prev, cny_rate_auto: false }));
  };

  const doCnyUpdate = async () => {
    const parsed = parseFloat(cnyValue);
    if (isNaN(parsed) || parsed < 6 || parsed > 8.5) { showFlash(isAr ? 'السعر يجب أن يكون بين 6.00 و 8.50' : 'Rate must be between 6.00 and 8.50'); return; }
    setSaving(prev => ({ ...prev, cny_usd_rate: true }));
    const before = settings.cny_usd_rate;
    const { data, error } = await sb.rpc('admin_set_cny_usd_rate', { p_rate: parsed });
    if (error) {
      console.error('[AdminSettings] admin_set_cny_usd_rate failed:', error.code || '', error.message);
      showFlash((isAr ? 'تعذّر الحفظ: ' : 'Could not save: ') + (error.message || ''));
    } else {
      await logAdminAction({ actorId: user.id, action: 'settings_update', entityType: 'admin_settings', entityId: 'cny_usd_rate', beforeState: before, afterState: data });
      showFlash(isAr ? 'تم الحفظ' : 'Saved');
    }
    await load();
    setSaving(prev => ({ ...prev, cny_usd_rate: false }));
    setConfirm(null);
  };

  const toggleMaintenance = async (enabled) => {
    await saveSetting('maintenance_mode', { enabled, message: maintMsg });
  };

  const toggleFoundingClub = async (enabled) => {
    await saveSetting('founding_club_active', { enabled });
  };

  // The request fee (299 SAR) launch switch. Keeps the amount and currency —
  // only `enabled` changes here. Takes effect for requests SUBMITTED from now
  // on: an existing request's fee row (or its absence) is fixed at insert.
  const doRequestFeeToggle = async () => {
    const cur = settings.managed_request_fee || {};
    await saveSetting('managed_request_fee', { ...cur, enabled: !(cur.enabled === true) });
    setConfirm(null);
  };

  const inviteAdmin = async () => {
    if (!inviteEmail.trim()) return;
    setSaving(prev => ({ ...prev, invite: true }));
    const done = () => setSaving(prev => ({ ...prev, invite: false }));

    const { data: existing } = await sb.from('profiles').select('id, role, full_name').ilike('email', inviteEmail.trim()).single();
    if (!existing) { showFlash(isAr ? 'المستخدم غير موجود' : 'User not found'); done(); return; }

    // Changing your own role is refused by guard_profile_sensitive_fields()
    // (self-demotion would lock everyone out of role management). Say so here
    // rather than letting the user discover it as a raw DB error.
    if (existing.id === user.id) {
      showFlash(isAr ? 'لا يمكنك تغيير دورك بنفسك' : 'You cannot change your own role');
      done(); return;
    }

    const before = { role: existing.role };
    // The error was previously discarded: after the super_admin role boundary
    // landed, a plain admin's refused update still flashed "role updated".
    const { error } = await sb.from('profiles').update({ role: inviteRole }).eq('id', existing.id);
    if (error) {
      showFlash((isAr ? 'تعذّر تعيين الدور: ' : 'Could not assign role: ') + (error.message || ''));
      done(); return;
    }

    await logAdminAction({ actorId: user.id, action: 'admin_role_assign', entityType: 'profile', entityId: existing.id, beforeState: before, afterState: { role: inviteRole } });
    setInviteEmail('');
    done();
    showFlash(isAr ? `تم تعيين الدور: ${inviteRole}` : `Role updated to ${inviteRole}`);
  };

  const feePct = settings.platform_fee_pct?.value ?? 5;
  const usdRate = settings.usd_sar_rate?.rate ?? 3.79;
  const cnyRate = settings.cny_usd_rate?.rate ?? 7.1;
  const cnyAuto = settings.cny_rate_auto || {};
  const maintEnabled = settings.maintenance_mode?.enabled ?? false;
  const foundingActive = settings.founding_club_active?.enabled ?? true;
  const reqFeeOn = settings.managed_request_fee?.enabled === true;
  const reqFeeAmount = settings.managed_request_fee?.amount ?? 299;

  return (
    <AdminRouteGuard user={user} profile={profile} lang={lang}>
      <AdminShell user={user} profile={profile} lang={lang}>
        <style>{CSS}</style>
        {flashMsg && <div className="st-flash">{flashMsg}</div>}

        <AdminConfirmDialog
          isOpen={confirm?.type === 'fee'}
          title={isAr ? 'تغيير رسوم المنصة' : 'Update Platform Fee'}
          description={isAr ? `تغيير الرسوم من ${feePct}% إلى ${feeValue}%` : `Change platform fee from ${feePct}% to ${feeValue}%`}
          confirmWord="CONFIRM"
          onConfirm={doFeeUpdate}
          onCancel={() => setConfirm(null)}
          isAr={isAr}
          danger
        />

        <AdminConfirmDialog
          isOpen={confirm?.type === 'request_fee'}
          title={isAr ? (reqFeeOn ? 'إيقاف رسوم تأكيد الطلب' : 'تفعيل رسوم تأكيد الطلب') : (reqFeeOn ? 'Turn the request fee OFF' : 'Turn the request fee ON')}
          description={isAr
            ? (reqFeeOn
              ? 'الطلبات المُدارة وطلبات «فريق مَعبر يبحث لك» الجديدة ستُرسل بلا رسوم. الطلبات الحالية لا تتغير.'
              : `كل طلب مُدار جديد وكل طلب «فريق مَعبر يبحث لك» سيُطلب منه ${reqFeeAmount} ر.س عند الإرسال، ولن يتحرك قبل الدفع.`)
            : (reqFeeOn
              ? 'New managed and "Maabar team finds a factory" requests will be submitted with no fee. Existing requests do not change.'
              : `Every new managed and "Maabar team finds a factory" request will be charged SAR ${reqFeeAmount} on submission, and will not move until paid.`)}
          confirmWord="CONFIRM"
          onConfirm={doRequestFeeToggle}
          onCancel={() => setConfirm(null)}
          isAr={isAr}
          danger
        />

        <AdminConfirmDialog
          isOpen={confirm?.type === 'rate'}
          title={isAr ? 'تغيير سعر صرف الدولار' : 'Update USD→SAR rate'}
          description={isAr
            ? `تغيير السعر من ${usdRate} إلى ${rateValue} ريال للدولار. يسري على العروض التي تُقبل والفواتير التي تُصدر من الآن؛ ما ثُبّت سابقاً يبقى على سعره.`
            : `Change the rate from ${usdRate} to ${rateValue} SAR per USD. Applies to offers accepted and invoices issued from now on; anything already locked keeps its rate.`}
          confirmWord="CONFIRM"
          onConfirm={doRateUpdate}
          onCancel={() => setConfirm(null)}
          isAr={isAr}
          danger
        />

        <AdminConfirmDialog
          isOpen={confirm?.type === 'cny_rate'}
          title={isAr ? 'تغيير سعر صرف اليوان' : 'Update CNY per USD'}
          description={isAr
            ? `تغيير السعر من ${cnyRate} إلى ${cnyValue} يوان للدولار. كل أسعار المتجر وكل سعر باليوان تتغيّر فورًا على هذا السعر. الحفظ اليدوي يوقف التحديث اليومي التلقائي حتى ترجعه.`
            : `Change the rate from ${cnyRate} to ${cnyValue} CNY per USD. Every store price and every CNY price changes at this rate straight away. Saving by hand stops the daily automatic update until you switch it back.`}
          confirmWord="CONFIRM"
          onConfirm={doCnyUpdate}
          onCancel={() => setConfirm(null)}
          isAr={isAr}
          danger
        />

        <div className="st-page" dir={isAr ? 'rtl' : 'ltr'}>
          <h1 className="st-page-title">{isAr ? 'الإعدادات' : 'Settings'}</h1>
          <p className="st-page-sub">{isAr ? 'إعدادات المنصة والفريق' : 'Platform configuration and team'}</p>

          {loading ? (
            <p style={{ color: 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY, fontSize: 13 }}>{isAr ? 'جارٍ التحميل...' : 'Loading…'}</p>
          ) : (
            <>
              {/* Platform Fee */}
              <SectionCard title={isAr ? 'رسوم المنصة' : 'Platform Fee'}>
                <div className="st-row">
                  <div>
                    <div className="st-label">{isAr ? 'نسبة الرسوم الحالية' : 'Current fee percentage'}</div>
                    {/* Honest label: this key is persisted to admin_settings but no
                        checkout path or edge function reads it yet, so it does not
                        actually price anything. Saying "applied to all new orders"
                        made an operator believe a change took effect. */}
                    <div className="st-sub" style={{ color: '#8B6914' }}>
                      {isAr ? '⚠️ محفوظة فقط — لا يقرأها مسار الدفع بعد' : '⚠️ Stored only — no checkout path reads it yet'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      className="st-input"
                      type="number"
                      min={0}
                      max={100}
                      step={0.1}
                      value={feeValue}
                      onChange={e => setFeeValue(e.target.value)}
                    />
                    <span style={{ fontSize: 13, fontFamily: FONT_BODY, color: 'rgba(0,0,0,0.55)' }}>%</span>
                    <button
                      className="st-btn st-btn-danger"
                      disabled={String(feePct) === feeValue}
                      onClick={() => setConfirm({ type: 'fee' })}
                    >
                      {isAr ? 'حفظ' : 'Save'}
                    </button>
                  </div>
                </div>
              </SectionCard>

              {/* Request fee (299 SAR) — the launch switch. This one DOES charge:
                  telr-create and the request insert trigger both read it. */}
              <SectionCard title={isAr ? 'رسوم تأكيد الطلب' : 'Request confirmation fee'}>
                <div className="st-row">
                  <div>
                    <div className="st-label">
                      {isAr ? `${reqFeeAmount} ر.س — الطلب المُدار و«فريق مَعبر يبحث لك»` : `SAR ${reqFeeAmount} — managed requests and "Maabar team finds a factory"`}
                    </div>
                    <div className="st-sub">
                      {isAr ? 'تُخصم من الطلب عند التنفيذ، وتُسترد يدويًا فقط إذا لم نجد مصنعًا موثّقًا خلال ١٠ أيام عمل.'
                        : 'Deducted from the order when the trader proceeds; refunded by hand only if no verified factory within 10 working days.'}
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="st-toggle"
                    checked={reqFeeOn}
                    onChange={() => setConfirm({ type: 'request_fee' })}
                    disabled={saving.managed_request_fee || !settings.managed_request_fee}
                  />
                </div>
              </SectionCard>

              {/* USD→SAR rate — this one prices real payments */}
              <SectionCard title={isAr ? 'سعر صرف الدولار' : 'USD → SAR rate'}>
                <div className="st-row">
                  <div>
                    <div className="st-label">{isAr ? 'ريال لكل دولار' : 'SAR per USD'}</div>
                    <div className="st-sub">
                      {isAr
                        ? 'يُثبَّت على العرض عند قبوله وعلى الفاتورة عند إصدارها — التغيير لا يمسّ ما ثُبّت قبله. يظهر للتاجر بجانب قيمة البضاعة.'
                        : 'Locked onto an offer when it is accepted and onto an invoice when it is issued — changing it never touches anything already locked. Shown to the trader beside the goods.'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      className="st-input"
                      type="number"
                      min={3.5}
                      max={4.5}
                      step={0.01}
                      value={rateValue}
                      onChange={e => setRateValue(e.target.value)}
                    />
                    <button
                      className="st-btn st-btn-danger"
                      disabled={saving.usd_sar_rate || String(usdRate) === rateValue}
                      onClick={() => setConfirm({ type: 'rate' })}
                    >
                      {isAr ? 'حفظ' : 'Save'}
                    </button>
                  </div>
                </div>
              </SectionCard>

              {/* CNY per USD — every store price converts at it; updated daily unless manual */}
              <SectionCard title={isAr ? 'سعر صرف اليوان' : 'CNY rate'}>
                <div className="st-row">
                  <div>
                    <div className="st-label">{isAr ? 'يوان لكل دولار' : 'CNY per USD'}</div>
                    <div className="st-sub">
                      {isAr
                        ? 'كل أسعار المتجر تُحوَّل من اليوان على هذا السعر: اليوان ÷ هذا السعر × سعر الدولار أعلاه. يتحدّث يوميًا من سعر البنك المركزي الأوروبي. أي تعديل يدوي يحوّل للوضع اليدوي.'
                        : 'Every store price converts CNY at this rate: CNY ÷ this rate × the USD rate above. Updated daily from the ECB rate. Saving a rate by hand switches to manual mode.'}
                    </div>
                    <div className="st-sub" style={{ marginTop: 6 }}>
                      {cnyAuto.manual
                        ? (isAr ? 'الوضع: يدوي — التحديث اليومي يُبلّغ فقط ولا يغيّر السعر.' : 'Mode: manual — the daily run only reports, it does not change the rate.')
                        : (isAr ? 'الوضع: تلقائي.' : 'Mode: automatic.')}
                      {cnyAuto.last_run_at && (isAr
                        ? ` آخر تشغيل: ${String(cnyAuto.last_run_at).slice(0, 16).replace('T', ' ')} — سعر السوق ${cnyAuto.last_market_rate ?? '-'} (${cnyAuto.last_outcome})`
                        : ` Last run: ${String(cnyAuto.last_run_at).slice(0, 16).replace('T', ' ')} — market ${cnyAuto.last_market_rate ?? '-'} (${cnyAuto.last_outcome})`)}
                    </div>
                    <button
                      className="st-btn"
                      style={{ marginTop: 8 }}
                      disabled={saving.cny_rate_auto}
                      onClick={() => doCnyMode(!cnyAuto.manual)}
                    >
                      {cnyAuto.manual
                        ? (isAr ? 'رجوع للتحديث التلقائي' : 'Switch back to automatic')
                        : (isAr ? 'تثبيت السعر يدويًا' : 'Pin the rate (manual)')}
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      className="st-input"
                      type="number"
                      min={6}
                      max={8.5}
                      step={0.01}
                      value={cnyValue}
                      onChange={e => setCnyValue(e.target.value)}
                    />
                    <button
                      className="st-btn st-btn-danger"
                      disabled={saving.cny_usd_rate || String(cnyRate) === cnyValue}
                      onClick={() => setConfirm({ type: 'cny_rate' })}
                    >
                      {isAr ? 'حفظ' : 'Save'}
                    </button>
                  </div>
                </div>
              </SectionCard>

              {/* Maintenance Mode */}
              <SectionCard title={isAr ? 'وضع الصيانة' : 'Maintenance Mode'}>
                <div className="st-row">
                  <div>
                    <div className="st-label">{isAr ? 'تفعيل وضع الصيانة' : 'Enable maintenance mode'}</div>
                    {/* No route guard, app shell, or edge function reads
                        maintenance_mode — toggling it currently blocks nobody.
                        Claiming otherwise is the most dangerous label on this page:
                        an operator could believe the platform is down when it is up. */}
                    <div className="st-sub" style={{ color: '#c0392b' }}>
                      {isAr ? '⚠️ غير مُفعّل بعد — لا يمنع أحداً فعلياً' : '⚠️ Not enforced yet — this blocks nobody'}
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="st-toggle"
                    checked={maintEnabled}
                    onChange={e => toggleMaintenance(e.target.checked)}
                    disabled={saving.maintenance_mode}
                  />
                </div>
                {maintEnabled && (
                  <>
                    <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.38)', fontFamily: FONT_BODY, marginBottom: 4 }}>{isAr ? 'رسالة الصيانة (اختياري)' : 'Maintenance message (optional)'}</div>
                    <input
                      className="st-input-wide"
                      value={maintMsg}
                      onChange={e => setMaintMsg(e.target.value)}
                      placeholder={isAr ? 'سيتم العودة قريباً...' : 'We\'ll be back shortly…'}
                      dir={isAr ? 'rtl' : 'ltr'}
                    />
                    <button
                      className="st-btn st-btn-primary"
                      style={{ marginTop: 10 }}
                      disabled={saving.maintenance_mode}
                      onClick={() => saveSetting('maintenance_mode', { enabled: maintEnabled, message: maintMsg })}
                    >
                      {isAr ? 'حفظ الرسالة' : 'Save message'}
                    </button>
                  </>
                )}
              </SectionCard>

              {/* Founding Club */}
              <SectionCard title={isAr ? 'نادي المؤسسين' : 'Founding Club'}>
                <div className="st-row">
                  <div>
                    <div className="st-label">{isAr ? 'تفعيل نادي المؤسسين' : 'Founding club active'}</div>
                    <div className="st-sub">{isAr ? 'يتيح التسجيل في النادي للمستخدمين الجدد' : 'Allows new users to join the founding club'}</div>
                  </div>
                  <input
                    type="checkbox"
                    className="st-toggle"
                    checked={foundingActive}
                    onChange={e => toggleFoundingClub(e.target.checked)}
                    disabled={saving.founding_club_active}
                  />
                </div>
              </SectionCard>

              {/* Admin Team Invite — super_admin only */}
              {isSuperAdmin && (
                <SectionCard title={isAr ? 'إدارة الفريق' : 'Admin Team'}>
                  <div className="st-label" style={{ marginBottom: 8 }}>{isAr ? 'تغيير دور مستخدم' : 'Assign admin role to a user'}</div>
                  <input
                    className="st-input-wide"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder={isAr ? 'البريد الإلكتروني...' : 'User email…'}
                    dir="ltr"
                    style={{ marginBottom: 8 }}
                  />
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select
                      value={inviteRole}
                      onChange={e => setInviteRole(e.target.value)}
                      style={{ padding: '7px 10px', background: 'var(--bg-subtle, #F5F2EE)', border: '1px solid rgba(0,0,0,0.09)', borderRadius: 7, fontSize: 12, color: 'rgba(0,0,0,0.75)', fontFamily: FONT_BODY, outline: 'none', minHeight: 34, cursor: 'pointer' }}
                    >
                      <option value="admin">admin</option>
                      <option value="super_admin">super_admin</option>
                      <option value="buyer">buyer</option>
                      <option value="supplier">supplier</option>
                    </select>
                    <button
                      className="st-btn st-btn-primary"
                      disabled={!inviteEmail.trim() || saving.invite}
                      onClick={inviteAdmin}
                    >
                      {saving.invite ? '…' : (isAr ? 'تعيين الدور' : 'Assign Role')}
                    </button>
                  </div>
                </SectionCard>
              )}

              {/* Audit Log */}
              <SectionCard title={isAr ? 'سجل المراجعة (آخر 100)' : 'Audit Log (last 100)'}>
                {auditLoading ? (
                  <p style={{ margin: 0, fontSize: 12, color: 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY }}>{isAr ? 'جارٍ التحميل...' : 'Loading…'}</p>
                ) : auditLog.length === 0 ? (
                  <p style={{ margin: 0, fontSize: 12, color: 'rgba(0,0,0,0.30)', fontFamily: FONT_BODY }}>{isAr ? 'لا توجد سجلات' : 'No audit entries'}</p>
                ) : (
                  auditLog.map(entry => (
                    <div key={entry.id} className="st-audit-row">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                        <div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(0,0,0,0.80)', fontFamily: FONT_BODY }}>{entry.action}</span>
                          {' '}
                          <span className="st-mono">{entry.entity_type}</span>
                        </div>
                        <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY, whiteSpace: 'nowrap', fontVariantNumeric: 'lining-nums' }}>{fmtTime(entry.created_at)}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', fontFamily: FONT_BODY, marginTop: 2 }}>
                        {entry.actor?.full_name || entry.actor?.email || (isAr ? 'النظام' : 'System')}
                        {entry.entity_id && <span className="st-mono" style={{ marginLeft: 6 }}>#{String(entry.entity_id).slice(0, 8)}</span>}
                      </div>
                      {entry.notes && (
                        <div
                          className="st-mono"
                          style={{
                            fontSize: 11,
                            color: 'rgba(0,0,0,0.60)',
                            marginTop: 4,
                            padding: '6px 8px',
                            background: 'rgba(0,0,0,0.03)',
                            borderRadius: 4,
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere',
                            direction: 'ltr',
                            textAlign: 'left',
                          }}
                        >
                          {entry.notes}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </SectionCard>
            </>
          )}
        </div>
      </AdminShell>
    </AdminRouteGuard>
  );
}
